import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  TableSortLabel,
  IconButton,
  Menu,
  MenuItem,
  FormControlLabel,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import HeaderFilter from "./HeaderFilter";
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { AppContext } from "../../contexts/AppContext";
import { useApi } from "../../hooks/useApi";

export default function DataGrid({ name='data', table, columns, RowContextMenu, exportFileName=null, title='', endPoint='', headerActions=[] }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [filteredRows, setFilteredRows] = useState([]);
  const [filters, setFilters] = useState({});
  const [selected, setSelected] = useState([]);
  const [sortConfig, setSortConfig] = useState({ field: null, direction: "asc" });
  const [visibleColumns, setVisibleColumns] = useState(
    columns.filter((col) => !col.invisible).reduce((acc, col) => ({ ...acc, [col.field]: true }), {})
  );
  const [visibilityAnchorEl, setVisibilityAnchorEl] = useState(null);
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  //const { fetchData } = useApi(); // Access the fetchData function from the api hook
  let apiUrl = endPoint?endPoint:`http://localhost/generic-app/products.php`;
  const { data, isLoading, refetch } = useApi({url:endPoint, key: name, params: { page:page, limit: rowsPerPage } });

useEffect(() => {
  refetch();
  console.log(data);
}, [page, rowsPerPage, filters, sortConfig]);

  /*useEffect(()=>{
    getData();
  },[page, rowsPerPage, filters, sortConfig])*/

  /*const getData = async () => {
    setLoading(true);
    setError(null);
    let url = apiUrl+'?';
    if (table) {
      url += `table=${table}`
    }
    url += `&page=${page}`
    url += `&limit=${rowsPerPage}`


    const f = transformFilters(filters);
    if (sortConfig.field) {
        url += `&sort=${sortConfig.field},${sortConfig.direction}`;
    }
    if (f.length > 0) {
        url += `&filters=${JSON.stringify(f)}`;
    }

     try {
         const data = await fetchData(url, { method: 'GET' });
          if (data && data.data) {
           setFilteredRows(data.data)
           setTotalCount(data.count)
          }
     } catch (error) {
         console.error('Error fetching data:', error);
         setError(error.message);
     }
    finally {
     setLoading(false);
    }
 }*/

  // --- Handlers ---
  const handlePageChange = (_, newPage) => setPage(newPage);

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (columnName, value) =>
    setFilters((prev) => ({ ...prev, [columnName]: value }));

  const handleSelectAll = (event) =>
    setSelected(event.target.checked ? filteredRows.map((row) => row.id) : []);

  const handleSelectRow = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );

  const handleSortChange = (field) => {
    const isAsc = sortConfig.field === field && sortConfig.direction === "asc";
    setSortConfig({ field, direction: isAsc ? "desc" : "asc" });
  };

  const toggleColumnVisibility = (field) =>
    setVisibleColumns((prev) => ({ ...prev, [field]: !prev[field] }));

  const handleVisibilityMenu = (event) => setVisibilityAnchorEl(event.currentTarget);
  const handleVisibilityMenuClose = () => setVisibilityAnchorEl(null);

  const exportToCSV = async () => {
    setLoading(true);
    //setError(null);

    try {
        const url = new URL(apiUrl);
        url.searchParams.set('table', table);
        url.searchParams.set('page', 1);
        url.searchParams.set('pageSize', 1000000);
        const f = transformFilters(filters);
        if (sortConfig.field) {
          url.searchParams.set('sort', `${sortConfig.field},${sortConfig.direction}`);
        }
        if (f.length > 0) {
          url.searchParams.set('filters', JSON.stringify(f));
        }

        const response = await fetch(url.toString());
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        const allData = data.data || data;

        const ws = XLSX.utils.json_to_sheet(allData);
        const wb = { Sheets: { 'data': ws }, SheetNames: ['data'] };
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const dataBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
        FileSaver.saveAs(dataBlob, exportFileName + '.xlsx');

    } catch (err) {
        //setError(err.message);
        console.error("Error fetching or exporting data:", err);
    } finally {
        setLoading(false);
    }
};

  // --- Render Column Visibility Menu ---
  const renderColumnVisibilityMenu = () => (
    <Menu
      anchorEl={visibilityAnchorEl}
      open={Boolean(visibilityAnchorEl)}
      onClose={handleVisibilityMenuClose}
    >
      {columns
        .filter((col) => !col.invisible)
        .map((col) => (
          <MenuItem key={col.field}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={visibleColumns[col.field]}
                  disabled={col.permanent}
                  onChange={() => toggleColumnVisibility(col.field)}
                />
              }
              label={col.headerName}
            />
          </MenuItem>
        ))}
    </Menu>
  );

  if (isLoading) {
    return <CircularProgress />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!data) {
    return <div>No data</div>;
  }

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 0 }}>
        <Box sx={{flex: 1}}>
          {title}
        </Box>
        <Box sx={{ display: "flex", p: 0}}>
          {headerActions.map((action, index) => (
            <Tooltip key={index} title={action.name}>
              <IconButton onClick={action.onClick} color="primary">
                  {action.icon}
              </IconButton>
            </Tooltip>
          ))}
          {exportFileName && <Box>
            <Tooltip title={'Export'}>
                  <IconButton onClick={exportToCSV} color="primary">
                      {loading ? <CircularProgress size={24} /> : <FileDownloadIcon />}
                  </IconButton>
              </Tooltip>
          </Box>}
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            {selected.length > 0 && (
              <Tooltip title="Actions">
                <IconButton onClick={(e) => setActionAnchorEl(e.currentTarget)}>
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
            )}
            <Menu
              anchorEl={actionAnchorEl}
              open={Boolean(actionAnchorEl)}
              onClose={() => setActionAnchorEl(null)}
            >
              <MenuItem onClick={() => console.log("Action 1")}>Action 1</MenuItem>
              <MenuItem onClick={() => console.log("Action 2")}>Action 2</MenuItem>
            </Menu>

            <IconButton onClick={handleVisibilityMenu}>
              <VisibilityIcon />
            </IconButton>
            {renderColumnVisibilityMenu()}
          </Box>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.length > 0 && selected.length < data.data.length}
                  checked={
                    selected.length === data.data.length && data.data.length > 0
                  }
                  onChange={handleSelectAll}
                />
              </TableCell>
              {columns
                .filter((col) => !col.invisible && visibleColumns[col.field])
                .map((col) => (
                  <TableCell
                    key={col.field}
                    align={col.align || "left"}
                    sortDirection={sortConfig.field === col.field ? sortConfig.direction : false}
                    sx={{ width: col.size || "auto" }}
                  >
                    {col.sortable === false ? (
                      col.headerName
                    ) : (
                      <TableSortLabel
                        active={sortConfig.field === col.field}
                        direction={
                          sortConfig.field === col.field ? sortConfig.direction : "asc"
                        }
                        onClick={() => handleSortChange(col.field)}
                      >
                        {col.headerName}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
              {/* Add Actions Column */}
              {RowContextMenu && <TableCell></TableCell>}
            </TableRow>
            <HeaderFilter
              columns={columns.filter((col) => !col.invisible && visibleColumns[col.field])}
              filters={filters}
              onFilterChange={handleFilterChange}
            />
          </TableHead>
          <TableBody>
            {data.data
              .map((row,index) => (
                <TableRow key={row.id} selected={selected.includes(row.id)}
                sx={{
                  paddingTop: 0, paddingBottom: 0,
                  backgroundColor: index % 2 === 0 ? "rgba(0, 0, 0, 0.05)" : "white", // Striped effect
                  "&:hover": {
                    backgroundColor: "rgba(137, 209, 233, 0.2)", // Light blue on hover
                  },
                }}
                >
                  <TableCell padding="checkbox" sx={{paddingTop: 0, paddingBottom: 0}}>
                    <Checkbox
                      checked={selected.includes(row.id)}
                      onChange={() => handleSelectRow(row.id)}
                    />
                  </TableCell>
                  {columns
                    .filter((col) => !col.invisible && visibleColumns[col.field])
                    .map((col) => (
                      <TableCell key={col.field} sx={{paddingTop: 0, 
                        paddingBottom: 0, 
                        width: col.size || "auto"}} 
                        align={col.align || "left"}
                      >
                        {col.render?col.render(row):row[col.field]}
                      </TableCell>
                    ))}
                  {/* Render Context Menu in Actions Column */}
                  {RowContextMenu && <TableCell sx={{paddingTop: 0, paddingBottom: 0}} align={"right"}>
                    <RowContextMenu
                      row={row}
                    />
                  </TableCell>}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={data.count}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25]}
      />
    </Box>
  );
}

function transformFilters(filterObject) {
  const filtersArray = [];

  for (const field in filterObject) {
    if (filterObject.hasOwnProperty(field)) {
      const filterData = filterObject[field];

      if (filterData.type === "between")
      {
        if (filterData.value && filterData.value.value1 !== "" ) {
            filtersArray.push({
                field: field,
                type: filterData.type,
                value: filterData.value,
                });
        }
      }
      else
      {
          if(filterData.value !== "")
          {
           filtersArray.push({
                field: field,
                type: filterData.type,
                value: filterData.value,
                });
          }
      }
    }
  }
  return filtersArray;
}